import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {addMonths, getDaysForMonthFill, isSameDay, isSameMonth} from '../core/utils/date.utils';
import {TitleService} from '../services/title.service';
import {MonthDayInformation, MonthService} from '../services/month.service';
import {formatDate, NgClass} from '@angular/common';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {MonthYearPipe, WeekdayPipe} from '../core/utils/pipe.utils';
import {MetaService} from '../services/meta.service';
import {ErrorService} from "../core/error/error.service";
import {ErrorComponent} from "../core/error/error.component";
import {MatTooltip} from "@angular/material/tooltip";
import {routeDateFormat, routeMonthFormat} from "../core/constants/route.formats";
import {LanguageService} from '../services/language.service';

@Component({
    selector: 'app-month',
    templateUrl: './month.component.html',
    imports: [
        TranslatePipe,
        MonthYearPipe,
        MatTooltip,
        NgClass,
        WeekdayPipe,
        ErrorComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./month.component.scss']
})
export class MonthComponent implements OnInit {

    currentMonth: Date = new Date();

    month: Date[][] = [];

    monthData: MonthDayInformation[] = [];

    moonLegendItems = [
        {key: 'FULL', phaseIds: [4]},
        {key: 'NEW', phaseIds: [0]},
        {key: 'WAXING', phaseIds: [1, 2, 3]},
        {key: 'WANING', phaseIds: [5, 6, 7]},
        {key: 'BLOOD', phaseIds: [8]},
        {key: 'BLUE', phaseIds: [9]}
    ];

    zodiacSigns = [
        'aries',
        'taurus',
        'gemini',
        'cancer',
        'leo',
        'virgo',
        'libra',
        'scorpio',
        'sagittarius',
        'capricorn',
        'aquarius',
        'pisces'
    ];

    error: boolean = false;

    constructor(
        private monthService: MonthService,
        private router: Router,
        private route: ActivatedRoute,
        private translate: TranslateService,
        private metaService: MetaService,
        private titleService: TitleService,
        private errorService: ErrorService,
        private language: LanguageService
    ) {
    }


    ngOnInit(): void {
        this.route.params.subscribe(params => {
            this.initRouteMonth(params);

            this.setTitleAndMeta();
            this.refresh();
        });

        this.errorService.getError().subscribe(error => {
            this.error = error;
        })

        this.translate.onLangChange.subscribe(_ => {
            this.refresh();
        })
    }

    initRouteMonth(params: Params) {
        const routeMonth = params['currentMonth'];
        if (routeMonth) {
            this.currentMonth = new Date(routeMonth);
        }
    }

    navigateBack() {
        const newMonth = addMonths(this.currentMonth, -1);
        this.navigateToMonth(newMonth);
    }

    navigateForward() {
        const newMonth = addMonths(this.currentMonth, 1);
        this.navigateToMonth(newMonth);
    }

    navigateToMonth(month: Date) {
        const newMonthString = formatDate(month, routeMonthFormat, 'en');
        this.router.navigateByUrl(this.language.route(`/month/${newMonthString}`));
    }

    getDayImages(day: Date): { sign: string; moon: string } | undefined {
        const found = this.getMonthDayInformation(day);

        if (!found) {
            return;
        }

        return {
            sign: `assets/images/month/${found.signId}.webp`,
            moon: `assets/images/moon/moon_${found.phase}.webp`
        };
    }

    isInCurrentMonth(day: Date) {
        return isSameMonth(day, this.currentMonth);
    }

    isCurrentDay(day: Date) {
        const today = new Date();
        return isSameDay(day, today);
    }

    navigateToDay(day: Date) {
        const newDayString = formatDate(day, routeDateFormat, 'en');
        this.router.navigateByUrl(this.language.route(`/day/${newDayString}`));
    }

    getImageForSign(signId: string) {
        return `assets/images/month/${signId}.webp`;
    }

    getDayToolTip(day: Date) {
        const found = this.getMonthDayInformation(day);

        if (!found) {
            return;
        }

        const sign = this.translate.instant(`SIGN.${found.signId.toUpperCase()}`);
        const phase = this.translate.instant(`PHASE.${found.phase}`);
        return `${sign}, ${phase}`;
    }

    private setTitleAndMeta() {
        const monthTransformed = new MonthYearPipe(this.translate).transform(this.currentMonth);
        const path = `/month/${formatDate(this.currentMonth, routeMonthFormat, 'en')}`;

        this.metaService.updateCanonicalUrl(path);

        this.translate.get('TITLE.MONTH_INFO', {date: monthTransformed}).subscribe((translated: string) => {
            this.titleService.updateTitleText(this.translate, translated);
        });

        this.translate.get('META.DESCRIPTION.MONTH').subscribe((translated: string) => {
            this.metaService.updateDescription(translated);
            this.metaService.updateSocialTags(
                this.translate.instant('TITLE.MONTH_INFO', {date: monthTransformed}),
                translated,
                path
            );
        });

        this.translate.get('META.KEYWORDS.MONTH', {date: monthTransformed}).subscribe((translated: string) => {
            this.metaService.updateKeywords(translated);
        });
    }

    private initMonth() {
        this.month = getDaysForMonthFill(this.currentMonth);
    }

    private fetchMonthData() {
        const firstArray: Date[] = this.month[0];
        const start = firstArray[0];

        const lastArray: Date[] = this.month[this.month.length - 1];
        const end = lastArray[lastArray.length - 1];

        this.monthData = [];
        const requestedMonth = this.currentMonth.getTime();

        this.monthService.getMonthInformation(start, end).subscribe(month => {
            if (requestedMonth !== this.currentMonth.getTime()) {
                return;
            }

            this.monthData = month;
        });
    }

    private getMonthDayInformation(day: Date): MonthDayInformation | undefined {
        const dayKey = formatDate(day, routeDateFormat, 'en');
        return this.monthData.find(item => item.day === dayKey);
    }

    private refresh() {
        this.initMonth();
        this.fetchMonthData();
    }

}
